import React, { useState, useEffect } from 'react'
import { Formik, FormikProps } from 'formik'
import * as Yup from 'yup'

import { InputModal, Input, Button, CheckboxInput, Flex, P, H4, Line, Close } from '../index'
import { WhatsAppIcon } from '../Icon/icon-component'
import './phone-collection-modal.scss'

// Common country codes
const countryCodes = [
  { code: '+91', country: 'India' },
  { code: '+1', country: 'USA' },
  { code: '+44', country: 'UK' },
  { code: '+61', country: 'Australia' },
  { code: '+65', country: 'Singapore' },
  { code: '+971', country: 'UAE' },
  { code: '+86', country: 'China' },
  { code: '+49', country: 'Germany' },
  { code: '+33', country: 'France' },
  { code: '+39', country: 'Italy' },
  { code: '+81', country: 'Japan' },
  { code: '+82', country: 'South Korea' },
  { code: '+7', country: 'Russia' },
  { code: '+55', country: 'Brazil' },
  { code: '+52', country: 'Mexico' },
  { code: '+966', country: 'Saudi Arabia' },
  { code: '+27', country: 'South Africa' },
  { code: '+234', country: 'Nigeria' },
  { code: '+20', country: 'Egypt' },
  { code: '+30', country: 'Greece' },
  { code: '+31', country: 'Netherlands' },
  { code: '+32', country: 'Belgium' },
  { code: '+34', country: 'Spain' },
  { code: '+36', country: 'Hungary' },
  { code: '+40', country: 'Romania' },
  { code: '+41', country: 'Switzerland' },
  { code: '+43', country: 'Austria' },
  { code: '+45', country: 'Denmark' },
  { code: '+46', country: 'Sweden' },
  { code: '+47', country: 'Norway' },
  { code: '+48', country: 'Poland' },
  { code: '+351', country: 'Portugal' },
  { code: '+353', country: 'Ireland' },
  { code: '+358', country: 'Finland' },
  { code: '+380', country: 'Ukraine' },
  { code: '+420', country: 'Czech Republic' },
  { code: '+421', country: 'Slovakia' },
  { code: '+60', country: 'Malaysia' },
  { code: '+62', country: 'Indonesia' },
  { code: '+63', country: 'Philippines' },
  { code: '+64', country: 'New Zealand' },
  { code: '+66', country: 'Thailand' },
  { code: '+84', country: 'Vietnam' },
  { code: '+90', country: 'Turkey' },
  { code: '+92', country: 'Pakistan' },
  { code: '+94', country: 'Sri Lanka' },
  { code: '+95', country: 'Myanmar' },
  { code: '+98', country: 'Iran' },
  { code: '+880', country: 'Bangladesh' },
  { code: '+977', country: 'Nepal' },
]

interface PhoneCollectionModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (values: {
    phone: string
    whatsappConsent: boolean
    setVerificationError: (error: string) => void
  }) => void
  onSkip: () => void
  onNotInterested: () => void
  onGenerateOTP?: (phone: string) => Promise<{ success: boolean; message: string }>
  onVerifyOTP?: (otp: string) => Promise<{ success: boolean; message: string }>
}

const phoneValidationSchema = Yup.object().shape({
  phone: Yup.string()
    .required('Phone number is required')
    .matches(/^[0-9+\s()-]{8,15}$/, 'Please enter a valid phone number'),
  countryCode: Yup.string().required('Country code is required'),
  whatsappConsent: Yup.boolean()
    .required('Consent is required')
    .oneOf([true], 'You must consent to receive WhatsApp messages'),
})

const otpValidationSchema = Yup.object().shape({
  otp: Yup.string()
    .required('OTP is required')
    .matches(/^[0-9]{6}$/, 'OTP must be 6 digits'),
})

const PhoneCollectionModal: React.FC<PhoneCollectionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onSkip,
  onNotInterested,
  onGenerateOTP,
  onVerifyOTP,
}) => {
  // Check if WhatsApp feature is enabled via environment variable
  const isWhatsAppEnabled = process.env.REACT_APP_WHATSAPP_ENABLED === 'true'

  const [otpSent, setOtpSent] = useState(false)
  const [verificationError, setVerificationError] = useState('')
  const [phone, setPhone] = useState('')
  const [whatsappConsent, setWhatsappConsent] = useState(false)
  const [resendDisabled, setResendDisabled] = useState(false)
  const [countdown, setCountdown] = useState(30)
  const [resendMessage, setResendMessage] = useState('')

  // Countdown timer effect
  useEffect(() => {
    let timer: NodeJS.Timeout | undefined

    if (resendDisabled && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1)
      }, 1000)
    }

    if (countdown === 0) {
      setResendDisabled(false)
      setCountdown(30)
    }

    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [resendDisabled, countdown])

  const initialValues = {
    phone: '',
    countryCode: '+91',
    whatsappConsent: false,
  }

  const otpInitialValues = {
    otp: '',
  }

  const handleGenerateOTP = async (values: {
    phone: string
    countryCode: string
    whatsappConsent: boolean
  }) => {
    try {
      // Store the phone and whatsapp consent values for later use
      const fullPhone = `${values.countryCode}${values.phone}`
      setPhone(fullPhone)
      setWhatsappConsent(values.whatsappConsent)

      // Always request OTP verification regardless of consent status
      if (onGenerateOTP) {
        const result = await onGenerateOTP(fullPhone)
        if (result.success) {
          setOtpSent(true)
          setVerificationError('')
          // Start countdown for resend button
          setResendDisabled(true)
        } else {
          setVerificationError(result.message)
        }
      } else {
        // Fallback if prop not provided
        setOtpSent(true)
        setVerificationError('')
      }
    } catch (error) {
      setVerificationError('Failed to send OTP. Please try again.')
    }
  }

  const handleResendOTP = async () => {
    try {
      // Clear any existing error message when resend button is clicked
      setVerificationError('')

      if (onGenerateOTP) {
        const result = await onGenerateOTP(phone)
        if (result.success) {
          // Start countdown
          setResendDisabled(true)
          setCountdown(30)
          // Show success message
          setResendMessage('New OTP sent successfully!')
          // Clear success message after 5 seconds
          setTimeout(() => {
            setResendMessage('')
          }, 5000)
        } else {
          setVerificationError(result.message)
        }
      } else {
        // Fallback if prop not provided
        setResendMessage('New OTP sent successfully!')
        setResendDisabled(true)
        setTimeout(() => {
          setResendMessage('')
        }, 5000)
      }
    } catch (error) {
      setVerificationError('Failed to send OTP. Please try again.')
    }
  }

  const handleVerifyOTP = async (values: { otp: string }) => {
    try {
      // Check if OTP is empty and show error
      if (!values.otp || values.otp.trim() === '') {
        setVerificationError('Please enter the verification code')
        return
      }

      if (onVerifyOTP) {
        const result = await onVerifyOTP(values.otp)
        if (result.success) {
          // Call onSubmit with the stored phone and whatsapp consent
          onSubmit({ phone, whatsappConsent, setVerificationError })
        } else {
          setVerificationError(result.message)
        }
      } else {
        // Fallback if prop not provided
        onSubmit({ phone, whatsappConsent, setVerificationError })
      }
    } catch (error) {
      setVerificationError('Invalid OTP. Please try again.')
    }
  }

  // Handler to clear verification error when OTP input changes
  const handleOTPChange = (
    setFieldValue: (field: string, value: string) => void,
    value: string,
  ) => {
    // Clear error message when user starts typing
    if (verificationError) {
      setVerificationError('')
    }
    setFieldValue('otp', value)
  }

  if (!isOpen || !isWhatsAppEnabled) return null

  return (
    <InputModal>
      <Flex variant='column' classes={['input-modal', 'phone-collection-modal']}>
        {/* This header will be hidden by CSS */}
        <Flex variant='space-between' classes={['header-section']}>
          <H4 text={otpSent ? 'Verify WhatsApp Number' : 'Complete Profile with WhatsApp Number'} />
          <Close height='24px' classes={['pointer']} onClick={onClose} />
        </Flex>
        <Line classes={['header-line']} />

        {!otpSent ? (
          // Phone Input Form
          <Formik
            initialValues={initialValues}
            validationSchema={phoneValidationSchema}
            onSubmit={handleGenerateOTP}
          >
            {(formik) => {
              const {
                values,
                touched,
                errors,
                dirty,
                handleChange,
                handleSubmit,
                setFieldValue,
              }: FormikProps<typeof initialValues> = formik

              return (
                <form onSubmit={handleSubmit}>
                  <Flex variant='column' classes={['reply-section']}>
                    <H4 text='WhatsApp Number' classes={['modal-title']} />
                    <P
                      variant='default-gray'
                      text='Please provide your WhatsApp number to complete your profile setup.'
                      classes={['description-text']}
                    />

                    <div className='whatsapp-layout'>
                      <div className='label-container'>
                        <label className='label'>WhatsApp Number</label>
                      </div>
                      <div className='field-container'>
                        <div className='country-select'>
                          <select
                            name='countryCode'
                            value={values.countryCode}
                            onChange={(e) => setFieldValue('countryCode', e.target.value)}
                            className='country-code-select'
                          >
                            {countryCodes.map(({ code, country }) => (
                              <option key={code} value={code}>
                                {country} ({code})
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className='number-input'>
                          <Input
                            name='phone'
                            placeholder='Enter your WhatsApp number'
                            type='text'
                            data-testid='phone-input'
                            required
                            value={values.phone}
                            handleChange={handleChange('phone')}
                            dirty={dirty}
                            errors={{ phone: !!errors && errors.phone ? errors.phone : null }}
                            touched={{ phone: !!touched && !!touched.phone }}
                          />
                          <span className='whatsapp-icon'>
                            <WhatsAppIcon />
                          </span>
                        </div>
                      </div>
                    </div>

                    <Flex variant='row' classes={['mt-16']}>
                      <CheckboxInput
                        labelText='I consent to receive WhatsApp messages at this number. *'
                        name='whatsappConsent'
                        value={values.whatsappConsent ? 'true' : ''}
                        handleChange={(e) => {
                          formik.setFieldValue('whatsappConsent', e.target.checked)
                        }}
                      />
                    </Flex>

                    {touched.whatsappConsent && errors.whatsappConsent && (
                      <P
                        variant='default-red'
                        text={String(errors.whatsappConsent)}
                        classes={['mt-8']}
                      />
                    )}

                    <P
                      variant='default-gray'
                      text='By giving consent, you agree to receive Credibila platform notifications and updates via WhatsApp.'
                      classes={['mb-16']}
                    />

                    <Line />

                    <Flex variant='column' classes={['phone-collection-actions']}>
                      <Button
                        text='Save WhatsApp Number'
                        variant='primary-btn'
                        handleClick={handleSubmit}
                      />
                      <Button text='Skip for Now' variant='secondary-btn' handleClick={onSkip} />
                    </Flex>

                    {/* Separate section for "Not interested" link */}
                    <div className='not-interested-section'>
                      <span className='not-interested-text'>
                        Not interested in providing phone number?
                      </span>
                      <a
                        className='click-here-link'
                        onClick={onNotInterested}
                        data-testid='not-interested-link'
                      >
                        Click here
                      </a>
                    </div>
                  </Flex>
                </form>
              )
            }}
          </Formik>
        ) : (
          // OTP Verification Form
          <Formik
            initialValues={otpInitialValues}
            validationSchema={otpValidationSchema}
            onSubmit={handleVerifyOTP}
          >
            {(formik) => {
              const {
                values,
                touched,
                errors,
                dirty,
                handleSubmit,
                setFieldValue,
              }: FormikProps<typeof otpInitialValues> = formik

              return (
                <form onSubmit={handleSubmit}>
                  <Flex variant='column' classes={['reply-section']}>
                    <P
                      variant='default-gray'
                      text='We have sent a 6-digit verification code to your WhatsApp number. Please enter it below.'
                      classes={['mb-16']}
                    />

                    <label className='input-label'>Verification Code</label>
                    <div className='otp-input-container'>
                      <Input
                        name='otp'
                        placeholder='Enter 6-digit code'
                        type='text'
                        data-testid='otp-input'
                        required
                        value={values.otp}
                        handleChange={(e) => handleOTPChange(setFieldValue, e.target.value)}
                        dirty={dirty}
                        errors={{ otp: null }}
                        touched={{ otp: !!touched && !!touched.otp }}
                      />
                    </div>

                    {verificationError && (
                      <P variant='default-red' text={verificationError} classes={['mt-8']} />
                    )}

                    {!verificationError && touched.otp && errors.otp && (
                      <P variant='default-red' text={errors.otp} classes={['mt-8']} />
                    )}

                    {resendMessage && !verificationError && !errors.otp && (
                      <P variant='default-blue' text={resendMessage} classes={['mt-8']} />
                    )}

                    <Line classes={['mt-16']} />

                    <Flex variant='column' classes={['mt-16', 'phone-collection-actions']}>
                      <Button
                        text='Verify WhatsApp Number'
                        variant='primary-btn'
                        handleClick={() => {
                          // Touch the OTP field to ensure validation errors show up
                          formik.setFieldTouched('otp', true, false)
                          // Validate the form
                          formik.validateForm().then(() => {
                            // If OTP is empty, show custom error
                            if (!formik.values.otp || formik.values.otp.trim() === '') {
                              setVerificationError('Please enter the verification code')
                            } else {
                              // Otherwise proceed with form submission
                              formik.handleSubmit()
                            }
                          })
                        }}
                        classes={['mb-16']}
                      />
                      <Button
                        text={resendDisabled ? `Resend OTP (${countdown}s)` : 'Resend OTP'}
                        variant='secondary-btn'
                        handleClick={handleResendOTP}
                        isDisabled={resendDisabled}
                        classes={['mb-16']}
                      />
                      <Button
                        text='Back'
                        variant='secondary-btn'
                        handleClick={() => {
                          setOtpSent(false)
                          setFieldValue('otp', '')
                        }}
                        classes={['mb-16']}
                      />
                    </Flex>
                  </Flex>
                </form>
              )
            }}
          </Formik>
        )}
      </Flex>
    </InputModal>
  )
}

export default PhoneCollectionModal
